import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input, Typography } from 'antd'
import { BuildingFile } from 'types/building'
import Validators from 'utils/validators'

import Button from 'components/atoms/Button'
import FormItem from 'components/atoms/FormItem'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

type Props = {
  file: BuildingFile
  existingNames?: string[]
  onCancel: () => void
  onSuccess: (newName: string) => void
}

export default function ModalEditFileName({
  file,
  existingNames = [],
  onCancel,
  onSuccess
}: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { name: fullName, extension } = file

  // Extract base name without extension
  const extSuffix = extension ? `.${extension}` : ''
  const initialBaseName = fullName.endsWith(extSuffix)
    ? fullName.slice(0, -extSuffix.length)
    : fullName

  const baseNameWatch = Form.useWatch('baseName', form)
  const [isDisabled, setIsDisabled] = useState(false)

  const handleSubmit = (values: { baseName: string }) => {
    const finalName = extension
      ? `${values.baseName.trim()}.${extension}`
      : values.baseName.trim()
    onSuccess(finalName)
  }

  useEffect(() => {
    if (baseNameWatch !== undefined) {
      form
        .validateFields(['baseName'])
        .then(() => {
          setIsDisabled(!baseNameWatch.trim())
        })
        .catch(() => {
          setIsDisabled(true)
        })
    }
  }, [baseNameWatch, form])

  return (
    <Modal onCancel={onCancel} width={600}>
      <Title title={t('building.renameFile')} bottomMargin="mb-8" />
      <Form
        form={form}
        onFinish={handleSubmit}
        initialValues={{ baseName: initialBaseName }}
        colon={false}
      >
        <div className="flex flex-col">
          <div className="flex items-start gap-2 min-h-[64px]">
            <FormItem
              name="baseName"
              className="flex-1 mb-0"
              rules={[
                {
                  validator: Validators.validateFileName(
                    t,
                    existingNames,
                    extension
                  )
                }
              ]}
            >
              <Input
                placeholder={t('building.enterFileName')}
                autoFocus
                className="h-[40px]"
                maxLength={255 - (extension ? extension.length + 1 : 0)}
              />
            </FormItem>
            {extension && (
              <div className="h-[40px] flex items-center">
                <Typography.Text className="text-[#021120] font-medium">
                  .{extension}
                </Typography.Text>
              </div>
            )}
          </div>
          <div className="flex gap-10 justify-center mt-4">
            <Button sizing="w-[140px] h-[50px]" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button
              sizing="w-[140px] h-[50px]"
              className="theme-bg-primary text-white"
              style={{ color: 'white' }}
              htmlType="submit"
              disabled={isDisabled}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  )
}

ModalEditFileName.defaultProps = {
  existingNames: []
}
